import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { authService } from '../services/auth';
import { organizationsService } from '../services/organizations';
import { User, MANAGER_ALLOWED_ROLES } from '../types';

interface AuthContextType {
  user: SupabaseUser | null;
  profile: User | null;
  organizationName: string | null;
  loading: boolean;
  isSuperAdmin: boolean;
  isTrainer: boolean;
  isClient: boolean;
  isLearner: boolean;
  isAllowedInManager: boolean; // true si le role peut acceder au Manager
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Debug mode - activer pour voir les logs
const DEBUG_AUTH = true;
const logAuth = (...args: unknown[]) => {
  if (DEBUG_AUTH) console.log('[Auth]', ...args);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [profile, setProfile] = useState<User | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isMounted = useRef(true);
  const isFirstEvent = useRef(true);

  const loadProfile = useCallback(async (userId: string): Promise<User | null> => {
    try {
      const userProfile = await authService.getUserProfile(userId);
      if (isMounted.current) {
        setProfile(userProfile);
        // Fetch org name for non-super-admin users
        if (userProfile?.organizationId) {
          organizationsService.getById(userProfile.organizationId).then((org) => {
            if (isMounted.current) setOrganizationName(org?.name || null);
          }).catch(() => {});
        } else {
          setOrganizationName(null);
        }
      }
      return userProfile;
    } catch (error) {
      console.error('Error loading profile:', error);
      if (isMounted.current) {
        setProfile(null);
        setOrganizationName(null);
      }
      return null;
    }
  }, []);

  const refreshProfile = async () => {
    if (user) {
      await loadProfile(user.id);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    isFirstEvent.current = true;

    logAuth('AuthProvider mounted, listening for auth changes...');

    // Utiliser uniquement onAuthStateChange comme source de vérité
    // Le premier événement détermine l'état initial
    const unsubscribe = authService.onAuthStateChange(async (newUser) => {
      logAuth('onAuthStateChange:', newUser?.email ?? 'null', isFirstEvent.current ? '(first event)' : '');

      if (!isMounted.current) return;

      setUser(newUser);

      if (newUser) {
        await loadProfile(newUser.id);
      } else {
        setProfile(null);
      }

      // Après le premier événement, on arrête le loading
      if (isFirstEvent.current) {
        logAuth('First auth event received, setting loading to false');
        setLoading(false);
        isFirstEvent.current = false;
      }
    });

    // Timeout de sécurité si onAuthStateChange ne se déclenche jamais
    const timeoutId = setTimeout(() => {
      if (isFirstEvent.current && isMounted.current) {
        logAuth('Safety timeout - no auth event received, setting loading to false');
        setLoading(false);
        isFirstEvent.current = false;
      }
    }, 5000);

    return () => {
      logAuth('AuthProvider unmounting...');
      isMounted.current = false;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [loadProfile]);

  const login = async (email: string, password: string) => {
    const supabaseUser = await authService.login(email, password);
    const userProfile = await loadProfile(supabaseUser.id);

    // Block unauthorized roles (learners) — logout and throw
    if (userProfile && !MANAGER_ALLOWED_ROLES.includes(userProfile.role)) {
      await authService.logout();
      setUser(null);
      setProfile(null);
      setOrganizationName(null);
      throw new Error('Acces non autorise. Veuillez vous connecter sur la plateforme d\'apprentissage.');
    }
  };

  const logout = async () => {
    await authService.logout();
    setProfile(null);
    setOrganizationName(null);
  };

  const isSuperAdmin = profile?.role === 'super_admin';
  const isTrainer = profile?.role === 'trainer';
  const isClient = profile?.role === 'org_admin';
  const isLearner = profile?.role === 'learner';
  const isAllowedInManager = profile ? MANAGER_ALLOWED_ROLES.includes(profile.role) : false;

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      organizationName,
      loading,
      isSuperAdmin,
      isTrainer,
      isClient,
      isLearner,
      isAllowedInManager,
      login,
      logout,
      refreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};
